import React, { useEffect, useMemo, useRef, useState } from 'react';
import Card from 'apollo-react/components/Card';
import CardContent from 'apollo-react/components/CardContent';
import CardActions from 'apollo-react/components/CardActions';
import Button from 'apollo-react/components/Button';
import Typography from './StyledTypography';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../axiosInstance';
import flowTypeImage from '../../assets/images/flow_type_image.png';

const GenericCard = ({
  label,
  buttonLabel,
  onButtonClick,
  children,
  imageId,
}) => {
  const { t, i18n } = useTranslation();
  const [resolvedImageUrl, setResolvedImageUrl] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const blobUrlRef = useRef(null);

  useEffect(() => {}, [i18n.language]);

  const imagePreviewEndpoint = useMemo(() => {
    if (!imageId) return null;
    return `/api/v1/files/admin/${imageId}/preview`;
  }, [imageId]);

  useEffect(() => {
    let isMounted = true;

    const cleanupBlobUrl = () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };

    const fetchImage = async () => {
      if (!imagePreviewEndpoint) {
        setResolvedImageUrl(flowTypeImage);
        setIsImageLoading(false);
        return;
      }

      try {
        setIsImageLoading(true);
        const response = await axiosInstance.get(imagePreviewEndpoint, {
          responseType: 'blob',
          withCredentials: true,
          validateStatus: (status) => status < 500,
        });

        if (!isMounted) return;

        if (response.status !== 200 || !response.data?.size) {
          cleanupBlobUrl();
          setResolvedImageUrl(flowTypeImage);
          setIsImageLoading(false);
          return;
        }

        cleanupBlobUrl();
        const blobUrl = URL.createObjectURL(
          new Blob([response.data], {
            type: response.headers['content-type'] || 'image/jpeg',
          })
        );
        blobUrlRef.current = blobUrl;
        setResolvedImageUrl(blobUrl);
        setIsImageLoading(false);
      } catch (error) {
        if (!isMounted) return;
        cleanupBlobUrl();
        setResolvedImageUrl(flowTypeImage);
        setIsImageLoading(false);
        console.warn('Could not load card image preview:', error);
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
      cleanupBlobUrl();
    };
  }, [imagePreviewEndpoint]);

  return (
    <Card
      style={{
        width: 375,
        minHeight: 372,
        display: 'flex',
        flexDirection: 'column',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundImage:
          !isImageLoading && resolvedImageUrl
            ? `url(${resolvedImageUrl})`
            : 'none',
        cursor: onButtonClick ? 'pointer' : undefined,
        overflow: 'hidden',
        borderRadius: 0,
        border: 'none',
      }}
      onClick={onButtonClick}
    >
      <CardContent
        style={{
          height: '100%',
          width: '100%',
          padding: 0,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {label && (
          <Typography
            variant="bodyDefault"
            style={{
              height: 65,
              fontSize: 29,
              paddingLeft: 16,
              display: 'flex',
              alignItems: 'center',
              fontWeight: 700,
              color: '#fff',
              fontFamily: 'Proxima Nova',
              lineHeight: 1,
              backgroundColor: 'rgba(0, 85, 135, 0.6)',
            }}
          >
            {label}
          </Typography>
        )}

        {children}
        <CardActions
          style={{
            width: '100%',
            justifyContent: 'center',
            padding: '25px 18px',
          }}
        >
          <Button
            variant="secondary"
            style={{
              background: '#fff',
              color: '#015FF1',
              fontWeight: 600,
              borderRadius: 50,
              border: 'none',
              minWidth: '100%',
              boxShadow: '0px 1px 1px 0px #00000040',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onButtonClick) onButtonClick();
            }}
          >
            {buttonLabel || t('view_details')}
          </Button>
        </CardActions>
      </CardContent>
    </Card>
  );
};

export default GenericCard;
